'use client'

import {
  AchievementFragment,
  AchievementTokenFragment,
  Achievement_Types_Enum,
  UserAchievementFragment
} from '@/gql/generated/graphql'
import { cn } from '@/lib/utils'
import { Button } from '@components/button'
import { Dialog } from '@components/dialog'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import { getAchievementImageOverride } from '@utils/achievements'
import { formatDate } from '@utils/common'
import { userProfileSetRoute } from '@utils/routes'
import { getSetImageUrl } from '@utils/sets'
import { ArrowRightIcon, BodyText, DetailText, LilSquigIcon, XIcon } from 'design-configuration/components'
import Lottie from 'lottie-react'
import Link from 'next/link'
import React, { useEffect, useState } from 'react'
import { useProfilePictureUrl, useUserDisplayName, useWalletOrUsername } from '../../../components/profile-context'
import { CommunityTag } from '../achievement-community-modal/community-tag'
import { ClaimAchievementButton } from '../achievements-grid/grid-items'
import honorConfetti from './assets/chromieConfetti.json'
import confetti from './assets/confetti.json'

type AchievementDetailsModalProps = {
  show: boolean
  onDismiss: () => void
  achievement: AchievementFragment
  userAchievement?: UserAchievementFragment
  token?: AchievementTokenFragment
  newlyClaimed?: boolean
  onClaimSuccess?: () => void
}

export const AchievementDetailsModal = ({
  show,
  onDismiss,
  achievement,
  userAchievement,
  token,
  newlyClaimed,
  onClaimSuccess
}: AchievementDetailsModalProps): React.ReactElement => {
  const [confettiAnimation, setConfettiAnimation] = useState<unknown>(null)
  const [isHovering, setIsHovering] = useState(false)
  const [isClaiming, setIsClaiming] = useState(false)
  const walletOrUsername = useWalletOrUsername()
  const profileName = useUserDisplayName()

  useEffect(() => {
    if (newlyClaimed) {
      setIsClaiming(true)
    }
  }, [newlyClaimed])

  useEffect(() => {
    if (isClaiming) {
      const animationUrl = achievement.type === Achievement_Types_Enum.Historical ? honorConfetti : confetti
      setConfettiAnimation(animationUrl)
    }
  }, [achievement.type, isClaiming])

  const emptyUrl = '/empty-pattern.png'
  const profilePictureUrl = useProfilePictureUrl()
  const imageOverride = getAchievementImageOverride(achievement.slug, profilePictureUrl)
  const imageUrl =
    userAchievement?.token?.media_url ??
    imageOverride ??
    achievement.image?.url ??
    getSetImageUrl(achievement.set ?? undefined) ??
    emptyUrl

  // Check if this achievement is related to a set and should show the "View Set" button
  const isSetRelated = !!achievement.set?.slug

  const showClaimButton = !userAchievement?.claimed && userAchievement?.earned_at && !isClaiming && !newlyClaimed

  const handleDismiss = () => {
    if (isClaiming && !newlyClaimed) {
      onClaimSuccess?.()
    }
    onDismiss?.()

    // if confetti animation playing make sure to stop it when closing modal
    if (confettiAnimation) {
      setConfettiAnimation(null)
    }
  }

  return (
    <Dialog
      open={show}
      onOpenChange={(open) => {
        if (!open) {
          handleDismiss()
        }
      }}
    >
      <Dialog.Overlay>
        {confettiAnimation ? (
          <Lottie
            animationData={confettiAnimation}
            loop={false}
            autoplay={true}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              zIndex: 1
            }}
            onComplete={() => {
              setConfettiAnimation(null)
            }}
          />
        ) : null}
      </Dialog.Overlay>
      <Dialog.Content>
        <div
          className="rounded-xs relative"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <div className="bg-surface flex flex-col md:h-[350px] md:flex-row">
            <div className="p-sm flex w-full justify-between md:hidden">
              <BodyText className="font-medium">{achievement.name}</BodyText>
              <button onClick={handleDismiss} className="focus:outline-none" tabIndex={-1}>
                <div className="relative h-4 w-4">
                  <XIcon
                    className="fill-on-surface absolute inset-0 h-full w-full transition-opacity duration-200"
                    style={{ opacity: isHovering ? 1 : 0 }}
                    width={16}
                    height={16}
                  />
                  <LilSquigIcon
                    className="absolute inset-0 h-full w-full transition-opacity duration-200"
                    style={{ opacity: isHovering ? 0 : 1 }}
                    width={16}
                    height={16}
                  />
                </div>
              </button>
            </div>
            {/* Image Section */}
            <div className="relative w-full md:h-full md:w-1/2">
              <div className="relative aspect-square w-full md:aspect-auto md:h-full">
                <ImageWithCustomLoader
                  src={imageUrl}
                  alt={achievement.name}
                  fill
                  className={cn('object-cover', !userAchievement && 'grayscale')}
                  sizes="(max-width: 767px) 90vw, 336px"
                  priority
                />
              </div>
            </div>

            {/* Details Section */}
            <div className="p-sm bg-surface flex w-full flex-col justify-between md:w-1/2">
              <div
                className={cn(
                  'pl-xs border-l-outline-variant hidden w-full justify-between border-l md:flex',
                  !token && userAchievement && 'border-l-transparent'
                )}
              >
                {token ? (
                  <div>
                    <DetailText className="text-on-surface-variant">{`${token?.project?.name} #${token?.invocation}`}</DetailText>
                    <DetailText className="text-on-surface-variant">{token?.project?.artist_name}</DetailText>
                  </div>
                ) : !userAchievement ? (
                  <div className="flex flex-col items-center justify-center">
                    <DetailText className="text-on-surface-variant">Achievement not earned</DetailText>
                  </div>
                ) : (
                  <div />
                )}
                <button onClick={handleDismiss} className="focus:outline-none" tabIndex={-1}>
                  <div className="relative h-4 w-4">
                    <XIcon
                      className="fill-on-surface absolute inset-0 h-full w-full transition-opacity duration-200"
                      style={{ opacity: isHovering ? 1 : 0 }}
                      width={16}
                      height={16}
                    />
                    <LilSquigIcon
                      className="absolute inset-0 h-full w-full transition-opacity duration-200"
                      style={{ opacity: isHovering ? 0 : 1 }}
                      width={16}
                      height={16}
                    />
                  </div>
                </button>
              </div>
              <div>
                <div className="hidden md:block">
                  <BodyText className="font-medium">{achievement.name}</BodyText>
                  <div
                    className={cn(
                      'my-xs h-[1px] w-full',
                      achievement.type === Achievement_Types_Enum.Historical
                        ? 'bg-gradient-chromie'
                        : 'bg-outline-variant'
                    )}
                  />
                </div>
                <BodyText className="text-on-surface-variant">{achievement.description}</BodyText>

                {userAchievement && userAchievement.claimed && userAchievement.earned_at ? (
                  <DetailText className="text-on-surface-variant mt-2xs">{`Certified on ${formatDate(
                    isClaiming ? new Date() : new Date(userAchievement.earned_at),
                    {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    }
                  )} • ${profileName}`}</DetailText>
                ) : null}
                <div className="mt-xs md:mt-sm gap-xs flex w-full flex-col">
                  <CommunityTag achievement={achievement} onDismiss={handleDismiss} className="w-fit" />
                  {isSetRelated ? (
                    <div className="mt-xs">
                      <Link href={userProfileSetRoute(walletOrUsername, achievement.set!.slug)} onClick={handleDismiss}>
                        <Button
                          colorTheme="outline"
                          iconRight={<ArrowRightIcon className="w-sm h-sm fill-inherit" />}
                          className="gap-x-xs flex w-full"
                        >
                          View set
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div />
                  )}
                  {showClaimButton && (
                    <ClaimAchievementButton
                      type="outline"
                      userAchievement={userAchievement}
                      onClaimSuccess={() => {
                        setIsClaiming(true)
                      }}
                      className="gap-x-xs w-full"
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="bg-surface-low py-xs px-sm border-t-outline-variant flex border-t md:hidden">
              {token ? (
                <DetailText className="text-on-surface-variant">
                  <span className="italic">{`${token?.project?.name} #${token?.invocation}`}</span> by{' '}
                  {token?.project?.artist_name}
                </DetailText>
              ) : !userAchievement ? (
                <DetailText className="text-on-surface-variant">Achievement not earned</DetailText>
              ) : (
                <div />
              )}
            </div>
          </div>
        </div>
      </Dialog.Content>
    </Dialog>
  )
}
